import { useRef, useState } from 'react';
import { IMAGEN_SET_MAX_BYTES, IMAGEN_TIPOS, type SetImagenes } from '@copathy/shared';
import { Boton } from './Boton';
import { Campo } from './Campo';
import { ImagenCuestionario } from './ImagenCuestionario';
import { Modal } from './Modal';
import { Equis } from './iconos';
import { ErrorApi } from '../lib/api';

/**
 * Los **sets de imágenes** para escalas (CC-04, migración 033).
 *
 * Un set se carga una vez —cinco caritas dibujadas a la medida— y lo usan muchas
 * escalas. Acá se crean, se les suben las imágenes y se desactivan.
 *
 * ⚠️ **Las imágenes se reparten con los puntos POR ORDEN**: la primera al punto
 * 1. Por eso la pantalla numera cada una a la vista y dice cuántas lleva: al
 * elegir el set en una escala, la cantidad tiene que coincidir con sus puntos, y
 * enterarse ahí es tarde.
 *
 * ⚠️ **Un set que ya usa algo publicado no se toca.** Lo impide
 * `tg_set_imagenes_inmutable`, porque cambiar la imagen 3 cambiaría lo que la
 * gente vio al responder. Se dice antes de intentarlo.
 */
export function ModalSets({
  abierto,
  sets,
  alCerrar,
  alCrear,
  alSubir,
  alQuitar,
  alDesactivar,
}: {
  abierto: boolean;
  sets: SetImagenes[];
  alCerrar: () => void;
  alCrear: (nombre: string) => Promise<void>;
  alSubir: (setId: number, archivo: File) => Promise<void>;
  alQuitar: (setId: number, itemId: number) => Promise<void>;
  alDesactivar: (setId: number) => Promise<void>;
}) {
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /** Contra qué set apunta el selector de archivo que se acaba de abrir. */
  const destino = useRef<number | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  async function conAviso(accion: () => Promise<void>) {
    setError(null);
    setOcupado(true);
    try {
      await accion();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No pudimos guardar el cambio.');
    } finally {
      setOcupado(false);
    }
  }

  function pedirArchivo(setId: number) {
    destino.current = setId;
    entrada.current?.click();
  }

  async function elegido(f: File | null) {
    const setId = destino.current;
    if (entrada.current) entrada.current.value = '';
    if (!f || setId === null) return;

    if (!(IMAGEN_TIPOS as readonly string[]).includes(f.type)) {
      setError('Tiene que ser PNG, JPG o WebP.');
      return;
    }
    if (f.size > IMAGEN_SET_MAX_BYTES) {
      setError(`Cada imagen puede pesar hasta ${IMAGEN_SET_MAX_BYTES / 1024 / 1024} MB.`);
      return;
    }
    await conAviso(() => alSubir(setId, f));
  }

  const activos = sets.filter((s) => s.activo);

  return (
    <Modal titulo="Sets de imágenes para escalas" ancho="ancho" abierto={abierto} alCerrar={alCerrar}>
      <input
        ref={entrada}
        type="file"
        accept={IMAGEN_TIPOS.join(',')}
        className="sr-only"
        onChange={(e) => void elegido(e.target.files?.[0] ?? null)}
      />

      <p className="mb-4 text-sm text-marino-500">
        Un set son las figuras de una escala, en orden: la primera es el punto 1. Se carga una vez y
        se puede usar en varias escalas.
      </p>

      {/* Crear */}
      <div className="mb-5 flex flex-wrap items-end gap-2 rounded-2xl border border-marino-100 bg-marino-50/50 p-4">
        <div className="min-w-[14rem] flex-1">
          <Campo
            etiqueta="Nombre del set nuevo"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
        </div>
        <Boton
          className="px-4 py-2.5 text-sm"
          disabled={nombre.trim().length < 3 || ocupado}
          onClick={() =>
            void conAviso(async () => {
              await alCrear(nombre.trim());
              setNombre('');
            })
          }
        >
          Crear set
        </Boton>
      </div>

      {activos.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-marino-200 px-4 py-6 text-center text-sm text-marino-400">
          Todavía no hay ningún set. Crea uno y súbele sus figuras, una por punto de la escala.
        </p>
      ) : (
        <ul className="space-y-3">
          {activos.map((s) => {
            const congelado = s.usosPublicados > 0;
            return (
              <li key={s.id} className="rounded-2xl border border-marino-100 p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-marino-800">{s.nombre}</span>
                  <span className="text-xs font-semibold text-marino-400">
                    {s.imagenes.length}{' '}
                    {s.imagenes.length === 1 ? 'imagen' : 'imágenes'}
                  </span>
                  {congelado && (
                    <span className="rounded-full bg-marino-100 px-2.5 py-0.5 text-xs font-semibold text-marino-600">
                      En uso · no se puede cambiar
                    </span>
                  )}
                  <div className="ml-auto flex gap-2">
                    {!congelado && (
                      <button
                        type="button"
                        disabled={ocupado || s.imagenes.length >= 11}
                        onClick={() => pedirArchivo(s.id)}
                        className="rounded-full border border-marino-200 bg-white px-3.5 py-1.5 text-sm
                          font-semibold text-marino-700 transition hover:bg-marino-50
                          disabled:cursor-not-allowed disabled:opacity-40
                          focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                      >
                        Agregar imagen
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={ocupado}
                      onClick={() => {
                        if (!confirm(`¿Sacar «${s.nombre}» de la lista de sets elegibles?`)) return;
                        void conAviso(() => alDesactivar(s.id));
                      }}
                      className="rounded-full px-3.5 py-1.5 text-sm font-semibold text-marino-500
                        transition hover:text-red-600 hover:underline disabled:opacity-40
                        focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                    >
                      Desactivar
                    </button>
                  </div>
                </div>

                {s.imagenes.length === 0 ? (
                  <p className="text-xs text-marino-400">
                    Sin imágenes. Un set vacío no se puede elegir en una escala.
                  </p>
                ) : (
                  <ul className="flex flex-wrap gap-2">
                    {s.imagenes.map((img) => (
                      <li key={img.id} className="relative">
                        <span className="block overflow-hidden rounded-2xl border border-marino-100 bg-white">
                          <ImagenCuestionario
                            ruta={`/api/cuestionarios/sets/imagenes/${img.id}`}
                            hay
                            alt={`Imagen ${img.orden}`}
                            className="h-16 w-16"
                          />
                        </span>
                        {/*
                          El número se ve encima de la figura, no debajo: es el
                          punto de la escala al que le toca, y es lo que hay que
                          poder comprobar de un vistazo.
                        */}
                        <span className="absolute -left-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-marino-800 text-[11px] font-bold text-white">
                          {img.orden}
                        </span>
                        {!congelado && (
                          <button
                            type="button"
                            disabled={ocupado}
                            onClick={() => void conAviso(() => alQuitar(s.id, img.id))}
                            aria-label={`Quitar la imagen ${img.orden}`}
                            className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full
                              border border-marino-200 bg-white text-marino-400 transition
                              hover:text-red-600 disabled:opacity-40
                              focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                          >
                            <Equis size={12} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p role="alert" className="mt-4 text-sm font-medium text-red-600">
          {error}
        </p>
      )}
    </Modal>
  );
}
